import React, { useState, useEffect, useContext } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import * as turf from "@turf/turf";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Polygon,
} from "@react-google-maps/api";
import { HiOutlineArrowLeft } from "react-icons/hi";
import { RiErrorWarningFill } from "react-icons/ri";
import {
  Camera,
  CameraResultType,
  CameraSource,
  CameraDirection,
} from "@capacitor/camera";
import { Geolocation } from "@capacitor/geolocation";
import CameraPreviewComponent from "./Component/CameraPreviewComponent";
import { Capacitor } from "@capacitor/core";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import * as attenadanceService from "../../Services/attendanceService";
import * as siteService from "../../Services/siteService";
import { BASE } from "../../routes";
import { parseWKTPolygon } from "../../Utils/geoUtils";
import { formatTime, formatTimeAMPM } from "../../Utils/timeUtils";
import { Loader, WarningPopup } from "../../Components/Common";
import {
  ATTENDANCE_TYPES,
  IN_PROGRESS,
  INCOMPLETE_TASK_WARNING,
  MESSAGES,
  PENDING,
  TASK_ERROR,
  TASK_WARNING,
} from "./constants";
import { GET_ALL_PAGINATION } from "../../Constants/Strings";
import { getTask } from "../../Services/taskServices";
import { UserContext } from "../../Context";
import { HiViewfinderCircle } from "react-icons/hi2";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";

const validationSchema = Yup.object().shape({
  site_id: Yup.string().when("withoutSite", {
    is: false,
    then: (schema) => schema.required(MESSAGES.SITE_SELECTION_REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
  remarks: Yup.string().when("withoutSite", {
    is: true,
    then: (schema) => schema.trim().required(MESSAGES.REMARKS_REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
  date: Yup.string().required(MESSAGES.DATE_REQUIRED),
  time: Yup.string().required(MESSAGES.TIME_REQUIRED),
});
const libraries = ["places"];

const AttendanceForm = ({ type }) => {
  const [photo, setPhoto] = useState(null);
  const [siteOptions, setSiteOptions] = useState([]);
  const [location, setLocation] = useState({ latitude: null, longitude: null });
  const [timeLeft, setTimeLeft] = useState(5 * 60);
  const [selectedSitePolygon, setSelectedSitePolygon] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [outsideGeofence, setOutsideGeofence] = useState(false);
  const [hasTodayTask, setHasTodayTask] = useState(false); // null = not checked yet
  const { isAdminOrHO, user } = useContext(UserContext);
  const containerStyle = { width: "100%", height: "300px" };
  const navigate = useNavigate();
  const isAndroid = Capacitor.getPlatform() === "android";
  const [taskWarning, setTaskWarning] = useState(false);
  const [taskCheckLoading, setTaskCheckLoading] = useState(true);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [siteLoaded, setSiteLoaded] = useState(false);
  const [locationLoaded, setLocationLoaded] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isSiteSelected, setIsSiteSelected] = useState(false);
  const [warningPopupMessage, setWarningPopupMessage] = useState("");
  const isLocationAvailable = Boolean(location.latitude && location.longitude);

  const LOCATION_MAX_RETRIES = 3;
  const LOCATION_RETRY_DELAY = 3000; // ms

  const checkUserInsideGeofence = (coords) => {
    if (!coords || coords.length < 3 || !isLocationAvailable) {
      return false;
    }
    const userPoint = turf.point([location.longitude, location.latitude]);
    const sitePolygon = turf.polygon([coords]);
    const bufferedPolygon = turf.buffer(sitePolygon, 30, { units: "meters" });
    return turf.booleanPointInPolygon(userPoint, bufferedPolygon);
  };

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const handleSiteChange = (e, form) => {
    const selectedSiteId = e.target.value;

    if (!isLocationAvailable && selectedSiteId) {
      alert(MESSAGES.LOCATION_REQUIRED);
      form.setFieldValue("site_id", "");
      navigate(BASE);
      return;
    }

    form.setFieldValue("site_id", selectedSiteId);
    setIsSiteSelected(Boolean(selectedSiteId));

    if (selectedSiteId === "NA") {
      setSelectedSitePolygon(null);
      setOutsideGeofence(false);
      return;
    }

    const selectedSite = siteOptions.find(
      (site) =>
        site.id === selectedSiteId || site.id.toString() === selectedSiteId,
    );

    if (selectedSite?.geofence) {
      const coords = parseWKTPolygon(selectedSite.geofence);
      setSelectedSitePolygon(coords);
      setOutsideGeofence(!checkUserInsideGeofence(coords));
    } else {
      setSelectedSitePolygon(null);
      setOutsideGeofence(false);
    }
  };

  const initialAttendanceValue = {
    site_id: "",
    withoutSite: false,
    remarks: "",
    date: new Date().toISOString().split("T")[0],
    time: new Date().toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };

  const takePhoto = async () => {
    try {
      const photo = await Camera.getPhoto({
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
        quality: 70,
        cameraDirection: CameraDirection.Front,
        width: 640,
        height: 800,
        preserveAspectRatio: true,
      });

      const response = await fetch(photo.webPath);
      const blob = await response.blob();
      const file = new File([blob], "selfie.jpg", { type: blob.type });
      setPhoto(file);
    } catch (error) {
      toast.error("Camera Error:", error);
    }
  };

  const getGeoLocation = async () => {
    let attempts = 0;
    let cancelled = false;

    while (attempts <= LOCATION_MAX_RETRIES && !cancelled) {
      try {
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });

        if (cancelled) return;

        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });

        setLocationLoaded(true);
        return;
      } catch (error) {
        attempts += 1;

        if (attempts > LOCATION_MAX_RETRIES) {
          alert(MESSAGES.LOCATION_REQUIRED);
          setLocationLoaded(true);
          navigate(BASE);
          return;
        }

        // wait before retry
        await new Promise((res) => setTimeout(res, LOCATION_RETRY_DELAY));
      }
    }

    return () => {
      cancelled = true;
    };
  };

  useEffect(() => {
    if (!isSiteSelected || !selectedSitePolygon) return;

    if (!isLocationAvailable) {
      alert(MESSAGES.LOCATION_REQUIRED);
      navigate(BASE);
      return;
    }

    const isInside = checkUserInsideGeofence(selectedSitePolygon);
    setOutsideGeofence(!isInside);
  }, [isSiteSelected, selectedSitePolygon, isLocationAvailable, navigate]);

  const handleSubmit = async (values) => {
    if (submitting) return;
    setSubmitting(true);

    if (!values.withoutSite && !values.site_id) {
      toast.error(MESSAGES.SITE_SELECTION_REQUIRED);
      return;
    }

    if (values.withoutSite && !values.remarks?.trim()) {
      setSubmitting(false);
      toast.error(MESSAGES.REMARKS_REQUIRED);
      return;
    }

    if (!photo) {
      setSubmitting(false);
      toast.error(MESSAGES.SELFIE_REQUIRED);
      return;
    }

    if (!location.latitude || !location.longitude) {
      toast.error(MESSAGES.LOCATION_REQUIRED);
      setSubmitting(false);
      return;
    }

    if (values.site_id && outsideGeofence) {
      toast.error(
        "You must be within the selected site's location to mark attendance.",
      );
      setSubmitting(false);
      return;
    }

    const formData = new FormData();

    if (!values.withoutSite && values.site_id) {
      formData.append("site", values.site_id);
    }
    if (values.withoutSite) {
      formData.append("remarks", values.remarks.trim());
    }
    formData.append("date", values.date);
    formData.append("is_backdated", 0);
    formData.append("type", type);
    formData.append("time", values.time);
    formData.append("photo", photo);
    formData.append("latitude", location.latitude);
    formData.append("longitude", location.longitude);

    try {
      const response = await attenadanceService.markAttenadance(formData);
      if (response.status === 201) {
        if (type === ATTENDANCE_TYPES.CHECKIN) {
          const checkinTime = response.data.checkin_time;
          toast.success(
            MESSAGES.SUBMITTED_ATTENDANCE(formatTimeAMPM(checkinTime)),
          );
        }
        if (type === ATTENDANCE_TYPES.CHECKOUT) {
          const checkoutTime = response.data.checkout_time;
          toast.success(
            MESSAGES.SUBMITTED_ATTENDANCE(formatTimeAMPM(checkoutTime)),
          );
        }
        navigate(BASE);
      } else {
        const msg = extractErrorMessage(response) || MESSAGES.FAILED_SUBMISSION;
        if (msg === MESSAGES.PREVIOUS_DAY_CHECKOUT_MISSING) {
          setWarningPopupMessage(msg);
        } else {
          toast.error(msg);
        }
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || MESSAGES.FAILED_SUBMISSION;
      if (msg === MESSAGES.PREVIOUS_DAY_CHECKOUT_MISSING) {
        setWarningPopupMessage(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
      setTaskWarning(false);
    }
  };

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await siteService.getSites(GET_ALL_PAGINATION);
        setSiteOptions(response.data.results);
        setSiteLoaded(true);
      } catch (error) {
        const msg = extractErrorMessage(error) || MESSAGES.SITES_ERROR;
        toast.error(msg);
      }
    };
    fetchSites();
    getGeoLocation();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev > 0) {
          return prev - 1;
        } else {
          clearInterval(timer);
          return 0;
        }
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (timeLeft === 0) {
      navigate(BASE);
    }
  }, [timeLeft]);

  useEffect(() => {
    if (
      isLoaded &&
      siteLoaded &&
      locationLoaded &&
      (isAdminOrHO || !taskCheckLoading)
    ) {
      setLoadingComplete(true);
    }
  }, [isLoaded, siteLoaded, locationLoaded, taskCheckLoading, isAdminOrHO]);

  const verifyTaskForToday = async () => {
    const todayDate = new Date().toISOString().split("T")[0];
    try {
      const response = await getTask({ start_date: todayDate });
      if (response.status === 200 && response.data.results.length > 0) {
        setHasTodayTask(true);

        const hasPendingOrInProgress = response.data.results.some(
          (task) => task.status === PENDING || task.status === IN_PROGRESS,
        );

        setTaskWarning(hasPendingOrInProgress);
      } else {
        setHasTodayTask(false);
        setTaskWarning(false);
      }
    } catch (err) {
      const msg = extractErrorMessage(err) || TASK_ERROR;
      toast.error(msg);
      setHasTodayTask(false);
      setTaskWarning(false);
    } finally {
      setTaskCheckLoading(false);
    }
  };

  useEffect(() => {
    verifyTaskForToday();
  }, []);

  if (loadError) return <div>{MESSAGES.MAP_LOADING_ERROR}</div>;

  if (!loadingComplete) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center gap-3 px-4 text-center">
        <Loader />
        {!locationLoaded ? (
          <>
            <p className="text-sm font-medium text-gray-700 animate-pulse">
              Fetching your location…
            </p>
            <p className="text-xs text-gray-500 max-w-xs">
              Please keep GPS enabled and stay at your location.
            </p>
          </>
        ) : (
          <p className="text-sm text-gray-600 animate-pulse">
            Preparing attendance form…
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-2 px-6 rounded-lg flex justify-center items-center">
      <WarningPopup
        title="Check-In Blocked"
        message={warningPopupMessage}
        onClose={() => setWarningPopupMessage("")}
      />
      <div className="bg-white rounded-lg p-6 pt-4 w-full max-w-3xl shadow-md">
        <div className="relative flex items-center justify-center h-10 mb-2">
          <button
            type="button"
            onClick={() => navigate(BASE)}
            className="absolute left-0 text-primary hover:text-secondary text-2xl"
            title="Back"
          >
            <HiOutlineArrowLeft />
          </button>

          <h2 className="text-2xl font-bold text-primary text-center leading-none">
            {type === ATTENDANCE_TYPES.CHECKIN ? "Check-In" : "Check-Out"} Form
          </h2>
        </div>

        {!isAdminOrHO && (
          <div className="mb-4">
            {taskCheckLoading && (
              <div className="flex justify-center">
                <div className="text-sm text-gray-500 animate-pulse">
                  Checking tasks...
                </div>
              </div>
            )}

            {!taskCheckLoading &&
              (type === ATTENDANCE_TYPES.CHECKIN
                ? hasTodayTask === false
                : taskWarning === true) && (
                <div className="flex justify-center">
                  <div className="mx-2 sm:mx-0 flex items-start gap-2 bg-yellow-100 text-yellow-800 border border-yellow-300 px-4 py-2 rounded-md text-sm font-medium shadow-sm max-w-md w-auto">
                    <RiErrorWarningFill className="w-5 h-5 flex-shrink-0 text-yellow-700 mt-0.5" />
                    <span className="flex-1">
                      {type === ATTENDANCE_TYPES.CHECKIN
                        ? TASK_WARNING
                        : INCOMPLETE_TASK_WARNING}
                    </span>
                  </div>
                </div>
              )}
          </div>
        )}

        <Formik
          initialValues={initialAttendanceValue}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue }) => (
            <>
              <Form className="space-y-4 text-secondary">
                <div>
                  <div className="pb-3">
                    <label
                      className={`flex items-start gap-3 transition-all duration-200 
                      ${
                        !user?.mark_attendance_without_site
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer hover:bg-gray-50 p-2 rounded-lg"
                      }
                    `}
                    >
                      <Field
                        type="checkbox"
                        name="withoutSite"
                        checked={values.withoutSite}
                        disabled={!user?.mark_attendance_without_site}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFieldValue("withoutSite", checked);

                          if (checked) {
                            setFieldValue("site_id", "");
                            setSelectedSitePolygon(null);
                            setOutsideGeofence(false);
                            setIsSiteSelected(false);
                          }
                        }}
                        className="mt-1 h-5 w-5 text-primary border-gray-300 rounded 
                        focus:ring-primary disabled:cursor-not-allowed"
                      />

                      <div className="flex flex-col">
                        <span
                          className={`font-medium 
                          ${
                            !user?.mark_attendance_without_site
                              ? "text-gray-400"
                              : "text-secondary"
                          }
                        `}
                        >
                          Mark attendance without site
                        </span>

                        <span className="md:text-xs text-[10px] text-gray-500">
                          (Use this option if you are not assigned to any site
                          today)
                        </span>

                        {!user?.mark_attendance_without_site && (
                          <span className="text-[10px] text-red-400 mt-1">
                            Permission disabled by HR
                          </span>
                        )}
                      </div>
                    </label>
                  </div>

                  {values.withoutSite && (
                    <div className="pb-3">
                      <label className="block font-medium">
                        Remarks / Reason
                      </label>
                      <Field
                        as="textarea"
                        name="remarks"
                        rows={2}
                        placeholder="Mention why you are marking attendance from a different location"
                        className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                      />
                      <ErrorMessage
                        name="remarks"
                        component="div"
                        className="text-red-600 text-sm"
                      />
                    </div>
                  )}

                  <label className="block font-medium">Site</label>
                  <Field name="site_id">
                    {({ field, form }) => (
                      <select
                        {...field}
                        onChange={(e) => handleSiteChange(e, form)}
                        disabled={values.withoutSite}
                        className={`w-full border rounded px-3 py-2 mt-1 ${
                          values.withoutSite
                            ? "bg-gray-100 cursor-not-allowed text-gray-500"
                            : "border-gray-300"
                        }`}
                      >
                        <option value="">Select a site</option>
                        {siteOptions.map((site) => (
                          <option key={site.id} value={site.id}>
                            {site.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                  <ErrorMessage
                    name="site_id"
                    component="div"
                    className="text-red-600 text-sm"
                  />
                  {values.site_id && outsideGeofence && (
                    <div className="mt-2 flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">
                      <RiErrorWarningFill className="mt-0.5 shrink-0" />
                      <span>You are outside the selected site’s geofence.</span>
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-center w-full gap-6">
                  <div className="w-5/6">
                    <label className="block font-medium">Date</label>
                    <Field
                      name="date"
                      type="date"
                      disabled
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1 text-gray-500"
                    />
                    <ErrorMessage
                      name="date"
                      component="div"
                      className="text-red-600 text-sm"
                    />
                  </div>

                  <div className="w-5/6">
                    <label className="block font-medium">Time</label>
                    <Field
                      name="time"
                      type="time"
                      disabled
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1 text-gray-500"
                    />
                    <ErrorMessage
                      name="time"
                      component="div"
                      className="text-red-600 text-sm"
                    />
                  </div>
                </div>
                {!isAndroid && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-2 py-2 w-full">
                    {/* First Row */}
                    <div className="flex justify-center sm:justify-start items-center gap-4 w-full sm:w-auto">
                      <label className="font-medium whitespace-nowrap">
                        Upload Photo
                      </label>
                      <button
                        type="button"
                        onClick={takePhoto}
                        className="bg-secondary text-white px-4 py-2 rounded hover:bg-primary"
                      >
                        Take Selfie
                      </button>
                    </div>

                    {/* Second Row */}
                    {photo && (
                      <div className="flex justify-center sm:justify-start items-center gap-4 w-full sm:w-auto">
                        <button
                          type="button"
                          className="text-primary underline flex items-center gap-1"
                          onClick={() => setShowPreview(true)}
                        >
                          <HiViewfinderCircle className="shrink-0" />
                          <span>View Selfie</span>
                        </button>
                        <p className="text-green-600 whitespace-nowrap">
                          Selfie captured ✔
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {isAndroid && (
                  <div className="flex justify-between gap-2">
                    <label className="block font-medium py-2">
                      Upload Photo
                    </label>
                    <div>
                      <CameraPreviewComponent onPhotoCapture={setPhoto} />
                    </div>
                  </div>
                )}

                {/* GOOGLE MAP CODE START */}
                {isLoaded && location.latitude && location.longitude && (
                  <div className="mt-4 rounded overflow-hidden border">
                    <GoogleMap
                      mapContainerStyle={containerStyle}
                      center={{
                        lat: location.latitude,
                        lng: location.longitude,
                      }}
                      zoom={14}
                      options={{
                        gestureHandling: "none",
                        draggable: false,
                        scrollwheel: false,
                        disableDoubleClickZoom: true,
                        keyboardShortcuts: false,
                      }}
                    >
                      {/* Marker for user's current location */}
                      <Marker
                        position={{
                          lat: location.latitude,
                          lng: location.longitude,
                        }}
                      />

                      {/* Polygon for selected site geofence */}
                      {selectedSitePolygon &&
                        selectedSitePolygon.length > 0 && (
                          <Polygon
                            paths={selectedSitePolygon.map(([lng, lat]) => ({
                              lat,
                              lng,
                            }))}
                            options={{
                              fillColor: "#00BFFF",
                              fillOpacity: 0.3,
                              strokeColor: "#1E90FF",
                              strokeOpacity: 0.8,
                              strokeWeight: 2,
                            }}
                          />
                        )}
                    </GoogleMap>
                  </div>
                )}
                {/* GOOGLE MAP FINISH */}

                <div className="text-center text-sm text-red-600 font-medium">
                  <p>Time left: {formatTime(timeLeft)}</p>
                </div>

                <div className="flex justify-between mt-8">
                  {/* CSS to be done based after viewing other pages */}
                  <button
                    type="button"
                    onClick={() => navigate(BASE)}
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <div className="relative inline-block">
                    <button
                      type="submit"
                      className="bg-primary text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                      disabled={
                        submitting || !locationLoaded || !isLocationAvailable
                      }
                    >
                      {submitting ? "Submitting..." : "Submit"}
                    </button>

                    {!isAdminOrHO &&
                      !taskCheckLoading &&
                      ((type === ATTENDANCE_TYPES.CHECKIN && !hasTodayTask) ||
                        (type === ATTENDANCE_TYPES.CHECKOUT &&
                          taskWarning)) && (
                        <RiErrorWarningFill
                          size={25}
                          className="absolute -top-4 -right-4 text-red-500 bg-transparent"
                        />
                      )}
                  </div>
                </div>
              </Form>

              {showPreview && photo && (
                <div className="fixed inset-0 z-[9999] bg-black bg-opacity-60 flex items-center justify-center">
                  <div className="bg-white rounded-lg shadow-lg w-full max-w-lg mx-4 sm:mx-6 max-h-[90vh] overflow-auto relative">
                    <div className="flex justify-between items-center px-4 py-2 border-b">
                      <h3 className="text-lg font-semibold text-primary">
                        Selfie Preview
                      </h3>
                      <button
                        onClick={() => setShowPreview(false)}
                        className="text-red-500 font-bold text-xl hover:text-red-700"
                      >
                        &times;
                      </button>
                    </div>
                    <div className="p-4 flex justify-center">
                      <img
                        src={URL.createObjectURL(photo)}
                        alt="Selfie Preview"
                        className="max-w-full max-h-[70vh] object-contain rounded border"
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AttendanceForm;
